import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  LayoutDashboard, 
  Image, 
  Layers, 
  User, 
  LogOut, 
  Plus,
  Trash2,
  Edit,
  Upload,
  X,
  BookOpen,
  GripVertical,
  Crop,
  ShoppingBag
} from 'lucide-react';
import { getApiUrl, makeRequest, resolveImageUrl } from '../../utils/api';
import ImageCropper from '../../components/ImageCropper';
import '../../styles/admin.css';

const AdminPortfolio = () => {
  const { token, logout } = useAuth();
  const [items, setItems] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [cropExistingImage, setCropExistingImage] = useState(null);

  const [formData, setFormData] = useState({
    category: '',
    treatmentName: '',
    description: '',
    beforeImage: '',
    order: 0
  });

  const fetchData = useCallback(async () => {
    try {
      const apiUrl = getApiUrl();
      const [portfolioRes, servicesRes] = await Promise.all([
        makeRequest('GET', `${apiUrl}/api/portfolio`),
        makeRequest('GET', `${apiUrl}/api/services`)
      ]);
      
      if (portfolioRes.ok) setItems(portfolioRes.data);
      if (servicesRes.ok) setServices(servicesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const apiUrl = getApiUrl();
      const result = await makeRequest('POST', `${apiUrl}/api/upload/image`, formDataUpload, {
        'Authorization': `Bearer ${token}`
      }, true);

      if (result.ok) {
        setFormData(prev => ({ ...prev, beforeImage: result.data.url }));
      } else {
        alert('Error uploading image: ' + (result.data.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Error uploading image: ' + error.message);
    }
    setUploading(false);
  };

  const handleCroppedImageUpload = async (croppedBlob) => {
    setUploading(true);
    setShowCropper(false);
    setCropExistingImage(null);
    
    const uploadData = new FormData();
    uploadData.append('file', croppedBlob, 'cropped-image.jpg');

    try {
      const apiUrl = getApiUrl();
      const result = await makeRequest('POST', `${apiUrl}/api/upload/image`, uploadData, {
        'Authorization': `Bearer ${token}`
      }, true);

      if (result.ok) {
        setFormData(prev => ({ ...prev, beforeImage: result.data.url }));
      } else {
        alert('Error uploading image: ' + (result.data.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Error uploading image: ' + error.message);
    }
    setUploading(false);
  };

  const handleCropExisting = () => {
    console.log('handleCropExisting called');
    console.log('- token from useAuth:', token ? 'present' : 'missing');
    console.log('- localStorage adminToken:', localStorage.getItem('adminToken') ? 'present' : 'missing');
    console.log('- formData.beforeImage:', formData.beforeImage);
    const resolvedUrl = resolveImageUrl(formData.beforeImage);
    console.log('- resolvedUrl:', resolvedUrl);
    setCropExistingImage(resolvedUrl);
    setShowCropper(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const apiUrl = getApiUrl();
    const url = editingItem 
      ? `${apiUrl}/api/portfolio/${editingItem.id}`
      : `${apiUrl}/api/portfolio`;
    
    const method = editingItem ? 'PUT' : 'POST';

    try {
      const result = await makeRequest(method, url, formData, {
        'Authorization': `Bearer ${token}`
      });

      if (result.ok) {
        setShowModal(false);
        setEditingItem(null);
        setFormData({
          category: '',
          treatmentName: '',
          description: '',
          beforeImage: '',
          order: 0
        });
        fetchData();
      } else {
        alert('Error saving item: ' + (result.data.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error saving item: ' + error.message);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      category: item.category,
      treatmentName: item.treatmentName,
      description: item.description,
      beforeImage: item.beforeImage,
      order: item.order
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      const apiUrl = getApiUrl();
      const result = await makeRequest('DELETE', `${apiUrl}/api/portfolio/${id}`, null, {
        'Authorization': `Bearer ${token}`
      });

      if (result.ok) {
        fetchData();
      } else {
        alert('Error deleting item');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error deleting item');
    }
  };

  const openAddModal = () => {
    setEditingItem(null);
    setFormData({
      category: services[0]?.id || '',
      treatmentName: '',
      description: '',
      beforeImage: '',
      order: items.length
    });
    setShowModal(true);
  };

  // Group items by category
  const groupedItems = items.reduce((acc, item) => {
    const cat = item.category;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  return (
    <div className="admin-layout">
      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="admin-sidebar-header">
          <h2>The Slay Station</h2>
          <p>Admin Panel</p>
        </div>

        <nav className="admin-nav">
          <Link to="/admin/dashboard" className="admin-nav-item">
            <LayoutDashboard size={20} />
            <span>Dashboard</span>
          </Link>
          <Link to="/admin/portfolio" className="admin-nav-item active">
            <Image size={20} />
            <span>Portfolio</span>
          </Link>
          <Link to="/admin/services" className="admin-nav-item">
            <Layers size={20} />
            <span>Services</span>
          </Link>
          <Link to="/admin/storefront" className="admin-nav-item">
            <ShoppingBag size={20} />
            <span>Curated Favorites</span>
          </Link>
          <Link to="/admin/business" className="admin-nav-item">
            <User size={20} />
            <span>Bio & Info</span>
          </Link>
        </nav>

        <div className="admin-sidebar-footer">
          <Link to="/" className="admin-nav-item" target="_blank">
            <BookOpen size={20} />
            <span>View Site</span>
          </Link>
          <button onClick={logout} className="admin-nav-item logout">
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        <header className="admin-header">
          <div>
            <h1>Portfolio</h1>
            <p>Manage your portfolio images</p>
          </div>
          <button onClick={openAddModal} className="admin-btn-primary">
            <Plus size={20} />
            Add Item
          </button>
        </header>

        <div className="admin-content">
          {loading ? (
            <div className="admin-loading">Loading...</div>
          ) : items.length === 0 ? (
            <div className="admin-empty">
              <Image size={48} />
              <h3>No portfolio items yet</h3>
              <p>Add your first portfolio item to get started</p>
              <button onClick={openAddModal} className="admin-btn-primary">
                <Plus size={20} />
                Add First Item
              </button>
            </div>
          ) : (
            <div className="admin-portfolio-sections">
              {Object.entries(groupedItems).map(([category, categoryItems]) => {
                const service = services.find(s => s.id === category);
                return (
                  <div key={category} className="admin-portfolio-section">
                    <h2 className="admin-section-title">{service?.name || category}</h2>
                    <div className="admin-portfolio-grid">
                      {categoryItems.map((item) => (
                        <div key={item.id} className="admin-portfolio-card">
                          <div className="admin-portfolio-image">
                            <img src={resolveImageUrl(item.beforeImage)} alt={item.treatmentName} />
                          </div>
                          <div className="admin-portfolio-info">
                            <h4>{item.treatmentName}</h4>
                            <p>{item.description}</p>
                          </div>
                          <div className="admin-portfolio-actions">
                            <button onClick={() => handleEdit(item)} className="admin-btn-icon">
                              <Edit size={18} />
                            </button>
                            <button onClick={() => handleDelete(item.id)} className="admin-btn-icon delete">
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Modal */}
      {showModal && (
        <div className="admin-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>{editingItem ? 'Edit Item' : 'Add Portfolio Item'}</h2>
              <button onClick={() => setShowModal(false)} className="admin-modal-close">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="admin-form">
              <div className="admin-form-group">
                <label>Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  required
                >
                  <option value="">Select a category</option>
                  {services.map(service => (
                    <option key={service.id} value={service.id}>{service.name}</option>
                  ))}
                </select>
              </div>

              <div className="admin-form-group">
                <label>Treatment Name</label>
                <input
                  type="text"
                  value={formData.treatmentName}
                  onChange={(e) => setFormData(prev => ({ ...prev, treatmentName: e.target.value }))}
                  placeholder="e.g., Hydrafacial Glow"
                  required
                />
              </div>

              <div className="admin-form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe this treatment..."
                  rows={3}
                />
              </div>

              <div className="admin-form-group">
                <label>Image</label>
                <div className="admin-image-upload">
                  {formData.beforeImage ? (
                    <div className="admin-image-preview">
                      <img src={resolveImageUrl(formData.beforeImage)} alt="Preview" />
                      <div className="admin-image-actions">
                        <button 
                          type="button" 
                          onClick={handleCropExisting}
                          className="admin-image-crop"
                          title="Crop Image"
                        >
                          <Crop size={16} />
                        </button>
                        <button 
                          type="button" 
                          onClick={() => setFormData(prev => ({ ...prev, beforeImage: '' }))}
                          className="admin-image-remove"
                          title="Remove Image"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="admin-upload-options-compact">
                      <button
                        type="button"
                        onClick={() => {
                          setCropExistingImage(null);
                          setShowCropper(true);
                        }}
                        className="admin-crop-upload-btn-small"
                        disabled={uploading}
                      >
                        <Crop size={18} />
                        <span>Crop & Upload</span>
                      </button>
                      <label className="admin-upload-btn-small">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          hidden
                        />
                        {uploading ? (
                          <span>Uploading...</span>
                        ) : (
                          <>
                            <Upload size={18} />
                            <span>Quick Upload</span>
                          </>
                        )}
                      </label>
                    </div>
                  )}
                </div>
                <p className="admin-form-hint">Or paste an image URL:</p>
                <input
                  type="url"
                  value={formData.beforeImage}
                  onChange={(e) => setFormData(prev => ({ ...prev, beforeImage: e.target.value }))}
                  placeholder="https://..."
                />
              </div>

              <div className="admin-form-actions">
                <button type="button" onClick={() => setShowModal(false)} className="admin-btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="admin-btn-primary">
                  {editingItem ? 'Save Changes' : 'Add Item'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Image Cropper Modal */}
      {showCropper && (
        <ImageCropper
          onCropComplete={handleCroppedImageUpload}
          onCancel={() => {
            setShowCropper(false);
            setCropExistingImage(null);
          }}
          existingImageUrl={cropExistingImage}
          parentToken={token}
        />
      )}
    </div>
  );
};

export default AdminPortfolio;
